import { z } from "zod";
import { Id } from "../common/ids.js";
import { defineRoute } from "../http/route.js";
const Email = z.email().max(254);
const Phone = z.string().regex(/^\+[1-9]\d{6,14}$/, "E.164 phone number");
const OneTimeCode = z.string().regex(/^\d{6}$/);
export const OidcProvider = z.enum(["google", "microsoft", "corppass"]).meta({ id: "OidcProvider" });
/** The client signing in; web gets the refresh token as an httpOnly cookie, desktop in the response body. */
export const DeviceInput = z
    .object({ client: z.enum(["web", "desktop"]), name: z.string().min(1).max(100) })
    .meta({ id: "DeviceInput" });
export const Tokens = z
    .object({
    tokenType: z.literal("Bearer"),
    accessToken: z.string(),
    expiresIn: z.number().int().positive().describe("Access token lifetime in seconds"),
    refreshToken: z
        .string()
        .optional()
        .describe("Returned only to the desktop client; browsers get an httpOnly cookie"),
})
    .meta({ id: "Tokens" });
export const SignInResult = z
    .discriminatedUnion("status", [
    z.object({ status: z.literal("authenticated"), tokens: Tokens, userId: Id, isNewUser: z.boolean() }),
    z.object({ status: z.literal("mfa_required"), challengeId: z.string() }),
])
    .meta({ id: "SignInResult" });
export const emailStart = defineRoute({
    id: "auth.email.start",
    method: "POST",
    path: "/v1/auth/email/start",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Send a one-time sign-in code to an email address",
    prd: ["IAM-1"],
    request: { body: z.object({ email: Email }) },
    responses: { 204: { description: "Code sent if the address can receive it" } },
    errors: ["VALIDATION_FAILED", "RATE_LIMITED"],
});
export const emailVerify = defineRoute({
    id: "auth.email.verify",
    method: "POST",
    path: "/v1/auth/email/verify",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Exchange an email and code for tokens; creates the user on first sign-in",
    prd: ["IAM-1"],
    request: { body: z.object({ email: Email, code: OneTimeCode, device: DeviceInput }) },
    responses: { 200: { description: "Signed in, or MFA required", schema: SignInResult } },
    errors: ["VALIDATION_FAILED", "INVALID_CODE", "RATE_LIMITED"],
});
export const phoneStart = defineRoute({
    id: "auth.phone.start",
    method: "POST",
    path: "/v1/auth/phone/start",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Send a one-time sign-in code by SMS through Twilio Verify",
    prd: ["IAM-1"],
    request: { body: z.object({ phone: Phone }) },
    responses: { 204: { description: "Code sent if the number can receive it" } },
    errors: ["VALIDATION_FAILED", "RATE_LIMITED"],
});
export const phoneVerify = defineRoute({
    id: "auth.phone.verify",
    method: "POST",
    path: "/v1/auth/phone/verify",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Exchange a phone number and code for tokens",
    prd: ["IAM-1"],
    request: { body: z.object({ phone: Phone, code: OneTimeCode, device: DeviceInput }) },
    responses: { 200: { description: "Signed in, or MFA required", schema: SignInResult } },
    errors: ["VALIDATION_FAILED", "INVALID_CODE", "RATE_LIMITED"],
});
export const oidcAuthorize = defineRoute({
    id: "auth.oidc.authorize",
    method: "GET",
    path: "/v1/auth/oidc/{provider}/authorize",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Redirect to Google, Microsoft, or Corppass with PKCE",
    prd: ["IAM-1"],
    request: {
        params: z.object({ provider: OidcProvider }),
        query: z.object({
            redirectUri: z.url().describe("Must be on the server's allowlist"),
            codeChallenge: z.string().min(43).max(128).describe("S256 challenge of the client's code verifier"),
            state: z.string().min(16).max(256).describe("Random per attempt; returned unchanged by the provider"),
        }),
    },
    responses: { 302: { description: "Redirect to the identity provider" } },
    errors: ["VALIDATION_FAILED", "NOT_FOUND"],
});
export const oidcCallback = defineRoute({
    id: "auth.oidc.callback",
    method: "POST",
    path: "/v1/auth/oidc/{provider}/callback",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Exchange the authorization code and verifier for tokens; a verified email joins the existing user with that email",
    prd: ["IAM-1"],
    request: {
        params: z.object({ provider: OidcProvider }),
        body: z.object({
            code: z.string().min(1),
            codeVerifier: z.string().min(43).max(128),
            redirectUri: z.url(),
            state: z.string().min(16).max(256),
            device: DeviceInput,
        }),
    },
    responses: { 200: { description: "Signed in, or MFA required", schema: SignInResult } },
    errors: ["VALIDATION_FAILED", "INVALID_CODE", "NOT_FOUND", "PERMISSION_DENIED", "DUPLICATE_REGISTRATION"],
});
export const mfaVerify = defineRoute({
    id: "auth.mfa.totp.verify",
    method: "POST",
    path: "/v1/auth/mfa/totp/verify",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Complete sign-in for a user with TOTP enabled",
    prd: ["IAM-1"],
    request: { body: z.object({ challengeId: z.string().min(1), code: OneTimeCode }) },
    responses: { 200: { description: "Signed in", schema: SignInResult } },
    errors: ["VALIDATION_FAILED", "INVALID_CODE", "RATE_LIMITED"],
});
export const refresh = defineRoute({
    id: "auth.refresh",
    method: "POST",
    path: "/v1/auth/refresh",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Rotate the refresh token and return a new access token",
    request: {
        body: z.object({
            refreshToken: z.string().optional().describe("Desktop only; browsers send the cookie"),
        }),
    },
    responses: { 200: { description: "New tokens; web receives a rotated refresh cookie", schema: Tokens } },
    errors: ["AUTH_REQUIRED", "TOKEN_EXPIRED"],
});
export const logout = defineRoute({
    id: "auth.logout",
    method: "POST",
    path: "/v1/auth/logout",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Revoke this device's session",
    prd: ["IAM-3"],
    responses: { 204: { description: "Signed out" } },
    errors: ["AUTH_REQUIRED"],
});
export const StepUpCodeSent = z
    .object({
    channel: z.enum(["email", "phone"]),
    to: z.string().describe("The address or number the code went to, masked"),
    expiresIn: z.number().int().positive().describe("Seconds the code stays valid"),
})
    .meta({ id: "StepUpCodeSent" });
export const stepUpCode = defineRoute({
    id: "auth.step-up.code",
    method: "POST",
    path: "/v1/auth/step-up/code",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Send a step-up code to the account's email, or its phone when it has no email; only for a person without an authenticator app, and good only for routes marked `step-up-any` (card 086)",
    prd: ["IAM-1"],
    responses: { 202: { description: "Sent", schema: StepUpCodeSent } },
    errors: ["AUTH_REQUIRED", "INVALID_TRANSITION", "RATE_LIMITED", "PERMISSION_DENIED"],
});
export const stepUp = defineRoute({
    id: "auth.step-up",
    method: "POST",
    path: "/v1/auth/step-up",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Take a code from the authenticator app, or, for a person without one, the code sent by `/v1/auth/step-up/code`; return an access token elevated for 5 minutes",
    request: { body: z.object({ code: OneTimeCode }) },
    responses: {
        200: { description: "Elevated access token; the refresh token is unchanged", schema: Tokens },
    },
    errors: ["AUTH_REQUIRED", "INVALID_CODE", "RATE_LIMITED", "PERMISSION_DENIED"],
});
export const switchEnterprise = defineRoute({
    id: "auth.switch-enterprise",
    method: "POST",
    path: "/v1/auth/switch-enterprise",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Make one of the caller's enterprises active and re-issue the tokens",
    prd: ["IAM-2"],
    request: {
        body: z.object({
            enterpriseId: Id,
            refreshToken: z.string().optional().describe("Desktop only; browsers send the cookie"),
        }),
    },
    responses: {
        200: { description: "Both tokens rotated, carrying the chosen enterprise", schema: Tokens },
    },
    errors: ["AUTH_REQUIRED", "NOT_FOUND", "TOKEN_EXPIRED"],
});
const PkceChallenge = z.string().regex(/^[A-Za-z0-9_-]{43}$/, "S256 challenge, base64url without padding");
export const createDesktopHandoff = defineRoute({
    id: "auth.desktop-handoff.create",
    method: "POST",
    path: "/v1/auth/desktop-handoff",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "From a web session, issue a 60-second single-use code that the desktop app redeems with the verifier behind the challenge it generated (card 016)",
    prd: ["IAM-1"],
    request: { body: z.object({ challenge: PkceChallenge }) },
    responses: {
        200: {
            description: "A code for the desktop app's deep link",
            schema: z.object({ code: z.string(), expiresIn: z.number().int().positive() }),
        },
    },
    errors: ["AUTH_REQUIRED", "VALIDATION_FAILED", "RATE_LIMITED"],
});
export const redeemDesktopHandoff = defineRoute({
    id: "auth.desktop-handoff.redeem",
    method: "POST",
    path: "/v1/auth/desktop-handoff/redeem",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "The desktop app exchanges a hand-off code and its verifier for a desktop session of the same user",
    prd: ["IAM-1"],
    request: {
        body: z.object({
            code: z.string().min(20).max(100),
            verifier: z.string().min(43).max(128),
            device: DeviceInput,
        }),
    },
    responses: {
        200: { description: "Signed in on the desktop", schema: z.object({ userId: Id, tokens: Tokens }) },
    },
    errors: ["VALIDATION_FAILED", "INVALID_CODE", "PERMISSION_DENIED"],
});
export const authRoutes = [
    emailStart,
    emailVerify,
    phoneStart,
    phoneVerify,
    oidcAuthorize,
    oidcCallback,
    mfaVerify,
    refresh,
    logout,
    stepUpCode,
    stepUp,
    switchEnterprise,
    createDesktopHandoff,
    redeemDesktopHandoff,
];
//# sourceMappingURL=auth.routes.js.map